import { useState, type FormEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import { useJoinWaitlist, useWaitlistPlaceholder, toWaitlistLanguage } from '@/hooks/useWaitlist';
import { ApiError } from '@/lib/api/client';

/**
 * WaitlistForm — email + optional note capture wired to the real backend (WAIT-02).
 *
 * Two visual sizes share submit logic:
 *  - `size="large"` (default) — used inside the bottom Waitlist section. Email
 *    input + an optional note textarea whose placeholder copy comes from the
 *    backend's A/B test. Card-shaped success state.
 *  - `size="compact"` — used inside WaitlistStrip (the slim strip after the
 *    Hero). Email-only, h-10, single-line success state. The strip is too
 *    tight for the textarea — the note is optional and the spec confirms it
 *    can be omitted (sent as null).
 *
 * Backend contract (predikt-backend):
 *   GET  /api/v1/waitlist/placeholder?lang=mk → { placeholder, variant, language }
 *   POST /api/v1/waitlist  body { email, note, variant, language } → { id, email, created_at }
 *
 * The placeholder query drives the **textarea** placeholder (variant A asks
 * "Why do you want to be one of the first?"; variant B asks "How did you hear
 * about us?"). The variant code echoes back on POST for A/B attribution.
 *
 * Error handling per spec:
 *  - 422 VALIDATION_ERROR: parse details.errors[].loc to find the offending
 *    field. Today only `email` and `note` are user-controlled, so we collapse
 *    to "invalid email" / "note too long" / generic.
 *  - 429 RATE_LIMIT_EXCEEDED: disabled submit + retry hint copy.
 *  - 503 DATABASE_UNAVAILABLE: retry button.
 *  - Network / 5xx / unknown: generic retry copy.
 *
 * Success state: `{ id, email, created_at }` returned but unused — the spec
 * says always-200 (idempotent on email), so the UI doesn't branch on
 * "already on the list". One success message covers both first-time + retry.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NOTE_MAX = 255;

type Size = 'large' | 'compact';

type WaitlistFormProps = {
  size?: Size;
};

type ApiErrorDetailField = { loc?: unknown[]; msg?: string; type?: string };

function extract422Field(error: unknown): 'email' | 'note' | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  const details = error.details as { errors?: ApiErrorDetailField[] } | undefined;
  const first = details?.errors?.[0];
  const path = Array.isArray(first?.loc) ? first.loc.map((p) => String(p)) : [];
  if (path.includes('email')) return 'email';
  if (path.includes('note')) return 'note';
  return null;
}

export default function WaitlistForm({ size = 'large' }: WaitlistFormProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const reduce = useReducedMotion();
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [clientInvalidEmail, setClientInvalidEmail] = useState(false);

  const placeholderQuery = useWaitlistPlaceholder();
  const join = useJoinWaitlist();

  const isCompact = size === 'compact';
  const inputId = isCompact ? 'waitlist-email-compact' : 'waitlist-email';
  const noteId = 'waitlist-note';
  const errorId = isCompact ? 'waitlist-error-compact' : 'waitlist-error';

  // Textarea placeholder = backend A/B copy (variant A or B) when available,
  // i18n fallback otherwise. Without a fallback the textarea reads as a void
  // when the placeholder query is loading or failing (e.g. CORS-blocked).
  const notePlaceholder =
    placeholderQuery.data?.placeholder ?? t('waitlist.form.notePlaceholderFallback');

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!EMAIL_RE.test(trimmedEmail)) {
      setClientInvalidEmail(true);
      return;
    }
    setClientInvalidEmail(false);
    const trimmedNote = note.trim();
    join.mutate({
      email: trimmedEmail,
      note: trimmedNote.length > 0 ? trimmedNote : null,
      variant: placeholderQuery.data?.variant ?? 'A',
      language: toWaitlistLanguage(i18n.language),
    });
  }

  // ── Success state ─────────────────────────────────────────────────────
  if (join.isSuccess) {
    if (isCompact) {
      return (
        <motion.div
          role="status"
          initial={reduce ? false : { opacity: 0, x: 4 }}
          animate={{ opacity: 1, x: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.3 }}
          className="text-ink inline-flex items-center gap-2 font-mono text-[12px]"
        >
          <span aria-hidden="true" className="text-orange font-semibold">
            ✓
          </span>
          <span>{t('waitlist.strip.successInline')}</span>
        </motion.div>
      );
    }
    return (
      <motion.div
        role="status"
        initial={reduce ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduce ? { duration: 0 } : { duration: 0.4 }}
        className="rounded-card border-paper/30 bg-paper/5 mx-auto max-w-[600px] border p-6 text-center"
      >
        <div className="text-orange mb-2 font-mono text-[11px] tracking-[0.22em] uppercase">
          {t('waitlist.success.eyebrow')}
        </div>
        <p className="text-paper m-0 mb-2 font-sans text-[28px] leading-tight font-semibold">
          {t('waitlist.success.headline')}
        </p>
        <p className="text-paper/70 m-0 font-mono text-[12px]">{t('waitlist.success.subhead')}</p>
      </motion.div>
    );
  }

  // ── Error classification ──────────────────────────────────────────────
  const apiError = join.error instanceof ApiError ? join.error : null;
  const failedField = extract422Field(apiError);
  const isRateLimit = apiError?.status === 429 || apiError?.code === 'RATE_LIMIT_EXCEEDED';
  const isDbDown = apiError?.status === 503 || apiError?.code === 'DATABASE_UNAVAILABLE';

  let errorMessage: string | null = null;
  if (clientInvalidEmail || failedField === 'email') {
    errorMessage = t('waitlist.form.invalidEmail');
  } else if (failedField === 'note') {
    errorMessage = t('waitlist.form.noteTooLong');
  } else if (isRateLimit) {
    errorMessage = t('waitlist.form.rateLimit');
  } else if (isDbDown) {
    errorMessage = t('waitlist.form.tryAgain');
  } else if (join.isError) {
    errorMessage = t('waitlist.form.serverError');
  }

  const isSubmitting = join.isPending;
  const isError = errorMessage !== null;
  // Rate limit: disable submit until user reloads/clears — caller can also tap
  // the input to clear, which `join.reset()` does on each onChange.
  const submitDisabled = isSubmitting || isRateLimit;

  // ── Compact form (Strip) ──────────────────────────────────────────────
  if (isCompact) {
    return (
      <div className="relative">
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap"
          noValidate
        >
          <label className="sr-only" htmlFor={inputId}>
            {t('waitlist.form.label')}
          </label>
          <input
            id={inputId}
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (clientInvalidEmail) setClientInvalidEmail(false);
              if (join.isError) join.reset();
            }}
            placeholder={t('waitlist.form.placeholder')}
            aria-invalid={isError}
            aria-describedby={isError ? errorId : undefined}
            className="rounded-pill bg-paper text-ink placeholder:text-dim border-rule focus-visible:ring-orange h-10 w-full flex-1 border px-4 font-mono text-[12px] focus-visible:ring-2 focus-visible:outline-none sm:w-[260px] sm:flex-initial"
          />
          <button
            type="submit"
            disabled={submitDisabled}
            className="bg-ink text-paper hover:bg-orange hover:text-ink rounded-pill inline-flex h-10 items-center justify-center gap-2 px-5 font-mono text-[11px] font-semibold tracking-[0.18em] whitespace-nowrap uppercase transition-colors disabled:opacity-60"
          >
            {isSubmitting ? t('waitlist.form.submitting') : t('waitlist.form.cta')}
            <span aria-hidden="true">→</span>
          </button>
        </form>
        {errorMessage ? (
          <p
            id={errorId}
            role="alert"
            className="text-red mt-2 font-mono text-[11px] tracking-[0.05em]"
          >
            {errorMessage}
          </p>
        ) : null}
      </div>
    );
  }

  // ── Large form (full Waitlist section) ────────────────────────────────
  return (
    <div className="relative">
      <form
        onSubmit={handleSubmit}
        className="mx-auto flex w-full max-w-[600px] flex-col gap-3"
        noValidate
      >
        <div className="flex w-full flex-col gap-3 sm:flex-row">
          <label className="sr-only" htmlFor={inputId}>
            {t('waitlist.form.label')}
          </label>
          <input
            id={inputId}
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (clientInvalidEmail) setClientInvalidEmail(false);
              if (join.isError) join.reset();
            }}
            placeholder={t('waitlist.form.placeholder')}
            aria-invalid={isError}
            aria-describedby={isError ? errorId : undefined}
            className="rounded-pill bg-paper text-ink placeholder:text-dim focus-visible:ring-orange h-14 flex-1 border-0 px-6 font-mono text-[14px] focus-visible:ring-2 focus-visible:outline-none"
          />
          <button
            type="submit"
            disabled={submitDisabled}
            className="bg-orange text-ink hover:bg-paper hover:text-ink rounded-pill inline-flex h-14 items-center justify-center gap-2.5 px-7 font-mono text-[13px] font-semibold tracking-[0.18em] whitespace-nowrap uppercase transition-colors disabled:opacity-60"
          >
            {isSubmitting ? t('waitlist.form.submitting') : t('waitlist.form.cta')}
            <span aria-hidden="true">→</span>
          </button>
        </div>
        <details className="group">
          <summary className="text-paper/50 hover:text-paper/80 cursor-pointer list-none font-mono text-[10px] tracking-[0.18em] uppercase transition-colors select-none">
            <span
              aria-hidden="true"
              className="mr-1.5 inline-block transition-transform group-open:rotate-90"
            >
              ›
            </span>
            {t('waitlist.form.noteLabel')}
          </summary>
          <label className="sr-only" htmlFor={noteId}>
            {t('waitlist.form.noteLabel')}
          </label>
          <textarea
            id={noteId}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (join.isError) join.reset();
            }}
            placeholder={notePlaceholder}
            maxLength={NOTE_MAX}
            rows={2}
            className="rounded-card bg-paper/[0.04] text-paper placeholder:text-paper/35 focus-visible:ring-orange border-paper/10 hover:border-paper/20 mt-2 w-full resize-none border px-4 py-2.5 font-mono text-[13px] leading-[1.45] transition-colors focus-visible:border-transparent focus-visible:ring-1 focus-visible:outline-none"
          />
        </details>
      </form>
      {errorMessage ? (
        <p
          id={errorId}
          role="alert"
          className="text-red mx-auto mt-3 max-w-[600px] text-center font-mono text-[11px] tracking-[0.05em]"
        >
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
